import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@platform/ui';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { routes } from '@platform/core/lib/routes';
import { ModuleSectionList } from '../shell/ModuleSectionList';
import { moduleSections } from '../shell/moduleSections';
import { useModulesQuery } from '../shell/useModulesQuery';
import { useModuleDescriptions } from '../shell/strings/useModuleDescriptions';
import { usePins } from '../shell/usePins';
import { appRoutes } from '../shell/appRoutes';
import { useShellNav } from '../shell/useShellNav';
import { useWorkspaceMeta } from '../shell/useWorkspaceMeta';
import '../shell/allModules.css';

/**
 * «Усі модулі» (прототип v4, UIUX patterns/search): заголовок і опис, пошук-пігулка з лічильником «N модулів» / «N з M» (Enter відкриває
 * перший збіг), розділи за групами верхнього меню — плитки модулів (іконка, назва, опис модуля, записи, зірка закріплення), наприкінці — «Система».
 * Фільтр групи й запит — в адресі (?group=, ?q=), їх ділить ліва колонка категорій.
 */
export function AllModulesPage() {
  const { t } = useTranslation();
  const label = useLabel();
  const navigate = useNavigate();
  const { ws } = useWorkspaceParams();
  const { workspace } = useWorkspaceMeta(ws);
  const { catalog, parliament } = useShellNav(ws);
  const [pinned, togglePin] = usePins(ws);
  const { group, q, setQ } = useModulesQuery();
  const describe = useModuleDescriptions(catalog, (catalog?.modules ?? []).filter((m) => m.installed).map((m) => m.key));
  const onSystem = (key: string) => navigate(key === 'administration' ? appRoutes.admin() : key === 'customization' ? appRoutes.customize() : routes.home());
  const base = { catalog, pinned, label, pinnedLabel: t('allModules.pinned'), systemLabel: t('allModules.system'), onSystem, describe, parliament };
  const sections = moduleSections({ ...base, q, group });
  const unique = (list: typeof sections) => new Set(list.flatMap((s) => s.cards.map((c) => c.key.split('/')[0]))).size;
  const total = unique(moduleSections({ ...base, q: '', group: '' }));
  const shown = unique(sections);
  const first = sections[0]?.cards[0];
  const openFirst = () => { if (first?.to) navigate(first.to); else first?.onOpen?.(); };
  return (
    <div className="am-page">
      <div className="am-head">
        <div className="am-head__text">
          <h1>{t('allModules.title')}</h1>
          <span className="muted">{t('allModules.subtitle', { ws: workspace?.name ?? '' })}</span>
        </div>
        <label className="am-search">
          <Icon name="search" size={15} />
          <input value={q} placeholder={t('allModules.search')} aria-label={t('allModules.search')} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') openFirst(); }} />
          <span className="am-search__count">{shown >= total ? t('shell.modulesCount', { count: total }) : t('allModules.shown', { shown, count: total })}</span>
        </label>
      </div>
      <ModuleSectionList sections={sections} onPin={togglePin} />
      {!sections.length && <div className="am-empty">{q ? t('allModules.empty', { q }) : t('allModules.emptyGroup')}</div>}
    </div>
  );
}
